import { Route, Routes, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from '@/context/useAuthStore';
import { ThemeProvider } from '../hooks/theme-provider';
import RequireSubscription from '@/hooks/use-subscription';

// Authorization
import ProtectedRoute from '../components/auth/ProtectedRoute';
import { ROUTE_PERMISSIONS } from '../constants/roles';
import Unauthorized from './Unauthorized';

// Landing pages
import SignIn from './SignIn';
import Brogrammers from './Landings/Brogrammers';
import Zenshkool from './Landings/Zenshkool';
import DatingLanding from './Landings/Dating';
import Hello from './Landings/hello';
import { OnboardingFlow } from '@/features/onboarding';

// Compliance pages
import Help from './Compliance/Help';
import Policy from './Compliance/Policy';
import ShippingDelivery from './Compliance/Shipping';
import TermsConditions from './Compliance/T&C';
import ReturnRefund from './Compliance/Return';
import ContactUs from './Compliance/Contact';
import Feedback from './Compliance/Feedback';
import AboutUs from './Compliance/About';
import Pricing from './Compliance/Pricing';
import Success from './Success';
import { SubscriptionPage } from '@/features/subscription';

// Student pages
import Home from './Home';
import Course from './Course';
import Courses from './Courses';
import Explore from './Explore';
import CourseCreationPage from './CreateCourse';
// Student - Quiz
import Quiz from './Quiz/Start';
import Placement from './Placement';
import PlacementForm from './PlacementForm';
import ReadBlog from './ReadBlog';
// Student - DSA pages
import DsaRoadmap from './DSA/Roadmap';
import MIK75Sheet from './DSA/MIK75Sheet';
import DsaGraph from './DSA/DSAGraph';
import AiBuddy from './DSA/AiBuddy';
// Student- Development pages
import Dev from './DEV/Dev';
import CreateBasket from './DEV/Create';
import Basket from './DEV/Basket';

// Admin pages
import Dashboard from './Admin/Dashboard';
import Classes from '../features/classroom/Classes';
import CreateClassPage from '../features/classroom/CreateClass';
import Students from './Admin/Students';
import Teachers from './Admin/Teachers';

// Teacher pages
import TeacherDashboard from './Teachers/Dashboard';
import TeacherClasses from './Teachers/MyClasses';
import ClassRoom from './Teachers/Classroom';
// Onboarding routes - no layout, but with providers
export function OnboardingRoutes() {
  const currentPath = window.location.pathname;

  const getComponent = () => {
    if (currentPath === '/onboard') return <OnboardingFlow />;

    return <OnboardingFlow />; // default fallback
  };

  return (
    <AuthProvider>
      <RequireSubscription>
        <ThemeProvider defaultTheme="light">{getComponent()}</ThemeProvider>
      </RequireSubscription>
    </AuthProvider>
  );
}

// Main app routes - will be wrapped with layout in App.js
export function MainAppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/student/home" replace />} />

      {/* Student Routes - Accessible to all authenticated users */}
      <Route path="/student" element={<ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.STUDENT}><Outlet /></ProtectedRoute>}>
        <Route index element={<Home />} />
        <Route path="home" element={<Home />} />
        <Route path="home/:conversationId" element={<Home />} />
        <Route path="courses" element={<Courses />} />
        <Route path="quiz" element={<Quiz />} />
        <Route path="create" element={<CourseCreationPage />} />
        <Route path="explore" element={<Explore />} />
        <Route path="dsa-roadmap" element={<DsaRoadmap />} />
        <Route path="dsa-sheet" element={<MIK75Sheet />} />
        <Route path="dsa-graph" element={<DsaGraph />} />
        <Route path="dsa-buddy" element={<AiBuddy />} />
      </Route>

      {/* Additional Student Routes (outside /student prefix) */}
      <Route path="/courses/:subjectName/*" element={<ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.STUDENT}><Course /></ProtectedRoute>} />
      <Route path="/hello" element={<ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.STUDENT}><Hello /></ProtectedRoute>} />

      {/* Legacy DSA route redirects */}
      <Route path="/dsa" element={<Navigate to="/student/dsa-roadmap" replace />} />
      <Route path="/student/dsa" element={<Navigate to="/student/dsa-sheet" replace />} />
      <Route path="/dsa-graph" element={<Navigate to="/student/dsa-graph" replace />} />
      <Route path="/dsa/buddy" element={<Navigate to="/student/dsa-buddy" replace />} />

      {/* Development Routes */}
      <Route path="/dev" element={<ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.STUDENT}><Outlet /></ProtectedRoute>}>
        <Route index element={<Dev />} />
        <Route path="create" element={<CreateBasket />} />
        <Route path=":id" element={<Basket />} />
      </Route>

      {/* Blog Routes */}
      <Route path="/blogs" element={<ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.STUDENT}><Outlet /></ProtectedRoute>}>
        <Route index element={<Placement />} />
        <Route path=":name" element={<ReadBlog />} />
      </Route>
      <Route path="/create-blogs" element={<ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.STUDENT}><PlacementForm /></ProtectedRoute>} />

      {/* Teacher Routes - Accessible to teachers and admins */}
      <Route path="/teacher" element={<ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.TEACHER}><Outlet /></ProtectedRoute>}>
        <Route path="dashboard" element={<TeacherDashboard />} />
        <Route path="classes" element={<TeacherClasses />} />
        <Route path="classes/*" element={<ClassRoom />} />
      </Route>

      {/* Admin Routes - Accessible to admins only */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.ADMIN}><Outlet /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="classes" element={<Classes />} />
        <Route path="classes/create" element={<CreateClassPage />} />
        <Route path="students" element={<Students />} />
        <Route path="staff" element={<Teachers />} />
        <Route path="*" element={<Dashboard />} />
      </Route>

      {/* Compliance Pages - Accessible to all authenticated users */}
      <Route path="/payment" element={<Success />} />
      <Route path="/help" element={<Help />} />
      <Route path="/privacy-policies" element={<Policy />} />
      <Route path="/shipping-delivery" element={<ShippingDelivery />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/return-refund" element={<ReturnRefund />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/subscription" element={<SubscriptionPage />} />

      {/* Unauthorized page */}
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Fallback - redirect to student home */}
      <Route path="/*" element={<Navigate to="/student/home" replace />} />
    </Routes>
  );
}

export function PublicRoutes() {
  return (
    <>
      <Routes>
        <Route path="/" element={<SignIn />} />
        <Route path="/dating" element={<DatingLanding />} />
        <Route path="/brogrammers" element={<Brogrammers />} />
        <Route path="/zenshkool" element={<Zenshkool />} />
        <Route path="/help" element={<Help isStandalone={true} />} />
        <Route path="/privacy-policies" element={<Policy isStandalone={true} />} />
        <Route path="/terms-conditions" element={<TermsConditions isStandalone={true} />} />
        <Route path="/return-refund" element={<ReturnRefund isStandalone={true} />} />
        <Route path="/about" element={<AboutUs isStandalone={true} />} />
        {/* <Route path="/pricing" element={<Pricing isStandalone={true} />} /> */}
        <Route path="/*" element={<SignIn />} />
      </Routes>
    </>
  );
}
